import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Avatar from "@mui/material/Avatar";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ButtonBase from "@mui/material/ButtonBase";
import { NavLink } from "react-router-dom";
import { AppTitle } from "../AppTitle";
import { AppBarNavLinksDesktop } from "./AppBar.Navlinks";
import { NotificationMenu } from "../Notification";
import QuickSearch from "./QuickSearch/QuickSearch";
import { useLocalSettings } from "../../hooks";
import { Box, Container, useMediaQuery } from "@mui/material";
import { useAuthContext } from "@src/AuthContext";
import { useUserState } from "@services/backend";
import { RedDot } from "./RedDot";

import { UserMenu } from "./UserMenu";
// const UserMenu = React.lazy(() => import("./UserMenu"));

export default function CustomAppBar() {
  const auth = useAuthContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { settings } = useLocalSettings();
  const isLeft = settings.hand === "left";

  const isMember = Boolean(auth.is_member);
  const { data: userState } = useUserState({ enabled: isMember });
  const hasNew = userState?.has_unseen_feed; // in future we can use or operator ..

  const [userMenu_anchorEl, setUserMenu_AnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(userMenu_anchorEl);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setUserMenu_AnchorEl(event.currentTarget);
  const handleMenuClose = () => setUserMenu_AnchorEl(null);

  /** "WeepyEyes" title & link to homepage */
  const AppLogo = (
    <NavLink to="/" className={"link-t"}>
      <AppTitle />
    </NavLink>
  );

  const { darkBottomBorder } = useScrolled(); // seperate to prevent re-render every scroll

  return (
    <AppBar
      sx={{
        padding: (t) => t.spacing(0.25, 0),
        backdropFilter: "blur(24px)",
        backgroundColor: (t) => (t.palette.mode === "dark" ? `${t.palette.background.paper}b3` : "rgba(255,255,255,0.75)"),
        backgroundImage: "unset",
        borderBottom: 1,
        // transition: "all .25s", // there's an annoying shift when menus / selects are opened
        borderBottomColor: darkBottomBorder ? "grey.900" : "grey.800",
      }}
      // color="transparent"
      elevation={4}
      position="fixed"
      aria-grabbed
    >
      <Container maxWidth="lg">
        <Toolbar variant="dense" style={{ flexDirection: isLeft ? "row-reverse" : "row", padding: 0 }}>
          {/* App Title / Logo */}
          <Box
            display={"flex"}
            alignItems={"center"}
            flex={isLeft ? "inherit" : undefined}
            sx={(theme) => ({ [theme.breakpoints.down("sm")]: { flex: 1 } })}
          >
            {AppLogo}
          </Box>

          {/* <BuyMeACoffee /> */}
          {!isMobile && isMember && <AppBarNavLinksDesktop />}

          {/* Buttons on right if logged in (non-members too) */}
          {auth.authed && (
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                flexDirection: isLeft ? "row" : "row-reverse",
                [theme.breakpoints.down("sm")]: isLeft ? { flex: 1 } : {},
              })}
            >
              {/* User avatar */}
              <Avatar
                style={{ borderRadius: "100%", margin: isLeft ? "0px 8px 0px 0px" : "0px 0px 0px 8px" }}
                alt="Name"
                src={auth.picture || undefined}
                onClick={handleMenuOpen}
                component={ButtonBase}
              >
                <AccountCircle />
              </Avatar>
              {/* User menu (when they click their avatar) */}
              {/* <Suspense fallback=""> */}
              <UserMenu
                anchorEl={userMenu_anchorEl}
                menuOpen={menuOpen}
                onMenuClose={handleMenuClose}
                isMobile={isMobile}
                isMember={isMember}
              />
              {/* </Suspense> */}
              {hasNew && isMobile && <RedDot />}

              {/* Notification Icon Menu & Quick Search */}
              {isMember && (
                <>
                  {/* {isMobile && <AppBarNavLinksMobile style={{ margin: isLeft ? "0px 8px 0px 0px" : "0px 0px 0px 8px" }} />} */}
                  <NotificationMenu style={{ margin: isLeft ? "0px 8px 0px 0px" : "0px 0px 0px 8px" }} />
                  <QuickSearch />
                  {/* {!isMobile && <AppBarNavLinksDesktop />} */}
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export const useScrolled = () => {
  const [darkBottomBorder, setDarkBottomBorder] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 20) {
        setDarkBottomBorder(true);
      } else {
        setDarkBottomBorder(false);
      }
    };
    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return { darkBottomBorder };
};
